import { getReversedMapping } from '@hourly/utilities';
import api from '~/api';
import type {
  FederalTaxClassificationResponse,
  IdentificationTypeResponse,
  PartnerAddressPayload,
  PartnerPayload,
  PartnerResponse,
} from '../dtos/partnerResponse';
import {
  FederalTaxClassification,
  Partner,
  TaxPayerIdentificationType,
} from '../models/partner.model';

interface PartnerUpdate {
  id: string;
  legalName: string;
  dba?: string | null;
  federalTaxClassification: FederalTaxClassification;
  taxpayerIdentificationType: TaxPayerIdentificationType;
  taxpayerIdentificationNumber: string;
  firstName: string;
  lastName: string;
  address: string;
  address2?: string | null;
  city: string;
  state: string;
  zipCode: string;
}

const TaxPayerIdentificationTypeMap: Record<
  IdentificationTypeResponse,
  TaxPayerIdentificationType
> = {
  fein: TaxPayerIdentificationType.EmployerIdentificationNumber,
  ssn: TaxPayerIdentificationType.SocialSecurityNumber,
};

const FederalTaxClassificationMap: Record<
  FederalTaxClassificationResponse,
  FederalTaxClassification
> = {
  individual: FederalTaxClassification.Individual,
  c_corporation: FederalTaxClassification.CCorporation,
  s_corporation: FederalTaxClassification.SCorporation,
  partnership: FederalTaxClassification.Partnership,
  trust_estate: FederalTaxClassification.TrustEstate,
  llc_c_corporation: FederalTaxClassification.LLCAsCCorporation,
  llc_s_corporation: FederalTaxClassification.LLCAsSCorporation,
  llc_partnership: FederalTaxClassification.LLCAsPartnership,
  other: FederalTaxClassification.Other,
};

export const getPartner = async (partnerId: string, partnerUserId: string): Promise<Partner> => {
  const fields = [
    'id',
    'name',
    'dba',
    'identification_type',
    'identification_number',
    'federal_tax_classification',
    'partner_users.id',
    'partner_users.first_name',
    'partner_users.last_name',
    'partner_users.email',
    'partner_users.phone_number',
    'address.address',
    'address.address2',
    'address.city',
    'address.state',
    'address.zipcode',
  ];

  const queryParams = new URLSearchParams({
    fields: fields.join(','),
  }).toString();

  const { data } = await api.get<PartnerResponse>(`/v1/partner/${partnerId}?${queryParams}`);

  const taxpayerIdentificationType = data?.identification_type
    ? TaxPayerIdentificationTypeMap[data.identification_type]
    : '';

  const federalTaxClassification = data?.federal_tax_classification
    ? FederalTaxClassificationMap[data.federal_tax_classification]
    : '';

  const partnerUser = data.partner_users.find((partner) => partner.id === partnerUserId);

  return new Partner({
    id: data.id,
    legalName: data.name,
    firstName: partnerUser?.first_name || '',
    lastName: partnerUser?.last_name || '',
    taxpayerIdentificationType,
    federalTaxClassification,
    dba: data.dba || '',
    taxpayerIdentificationNumber: data.identification_number || '',
    email: partnerUser?.email || '',
    phoneNumber: partnerUser?.phone_number || '',
    address: data.address.address || '',
    address2: data.address.address2 || '',
    city: data.address.city || '',
    state: data.address.state || '',
    zipCode: data.address.zipcode || '',
  });
};

export const updatePartner = async (partner: Partial<PartnerUpdate>): Promise<void> => {
  const identificationTypeReversed = getReversedMapping(TaxPayerIdentificationTypeMap);
  const federalTaxClassificationReversed = getReversedMapping(FederalTaxClassificationMap);

  const federalTaxClassification = partner?.federalTaxClassification
    ? federalTaxClassificationReversed[partner.federalTaxClassification as FederalTaxClassification]
    : undefined;

  const identificationType = partner?.taxpayerIdentificationType
    ? identificationTypeReversed[partner.taxpayerIdentificationType as TaxPayerIdentificationType]
    : undefined;

  let address: Partial<PartnerAddressPayload> | undefined = undefined;
  const addressKeys = ['address', 'address2', 'city', 'state', 'zipCode'] as const;
  const hasAddress = addressKeys.some((key) => partner?.[key]);

  if (hasAddress) {
    address = {
      address: partner?.address,
      address2: partner?.address2 ? partner.address2 || null : undefined,
      city: partner?.city,
      state: partner?.state,
      zipcode: partner?.zipCode,
    };
  }

  const payload: PartnerPayload = {
    name: partner.legalName,
    dba: partner?.dba,
    federal_tax_classification: federalTaxClassification,
    identification_type: identificationType,
    identification_number: partner.taxpayerIdentificationNumber,
    first_name: partner.firstName,
    last_name: partner.lastName,
    address,
  };

  return await api.post(`/v1/partner/${partner.id}/details`, payload);
};
